import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Grid,
  Button,
  IconButton,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useSelector } from "react-redux";
import MicIcon from "@mui/icons-material/Mic";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ReusableDialog from "../../../../common/dialog/dialog";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";

const CauseForWitness = ({ updateFormData, nextStep, prevStep, formData, witness, date, time, location }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [witnessData, setWitnessData] = useState([]);
  const [role, setRole] = useState("Involved");
  const [category, setCategory] = useState("Adult");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const [incidentRecords, setIncidentRecords] = useState([]); // Stores submitted voice records
  const [comment, setComment] = useState("");
  const [selectedUser, setSelectedUser] = useState(null); // Store selected user object
  const [users, setUsers] = useState([]);
  const [incidentTime, setIncidentTime] = useState(() => {
    const now = new Date();
    return time || now.toTimeString().slice(0, 5); // Extracts HH:MM format
  });
  const [dateInformed, setDateInformed] = useState(date || "");
  const [place, setPlaced] = useState(location || "");

  useEffect(() => {
    if (witness && witness.length > 0) {
      setWitnessData(witness);
    }
  }, [witness]);

  useEffect(() => {
    if (formData && formData.length > 0) {
      setIncidentRecords(formData);
    }
  }, [formData]);

  // Handle role selection (Involved/Witness)
  const handleRoleChange = (event, newRole) => {
    if (newRole !== null) {
      setRole(newRole);
    }
  };

  // Handle category selection (Adult/Child)
  const handleCategoryChange = (event, newCategory) => {
    if (newCategory !== null) {
      setCategory(newCategory);
    }
  };

  // Handle form submission - Add new witness to table
  const handleProceed = () => {
    if (!selectedUser) {
      alert("Please select a user.");
      return;
    }

    // Add new witness with user ID
    setWitnessData((prevData) => [
      ...prevData,
      { userId: selectedUser.id, name: selectedUser.username, role, category },
    ]);

    // Reset form fields
    setSelectedUser(null);
    setRole("Involved");
    setCategory("Adult");

    // Close Dialog
    setDialogOpen(false);
  };
  // Start Recording
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunks.current = [];

    mediaRecorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioURL(audioUrl);
    };

    mediaRecorder.start();
    setRecording(true);
  };

  // Stop Recording
  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  // Handle Submit - Save Recorded Entry
  const handleSubmitRecord = async () => {
    try {
      let uploadedAudioUrl = null;

      // Upload audio if available
      if (audioURL) {
        const response = await fetch(audioURL);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append("audio", blob, "recording.wav");

        const uploadRes = await fetch("/api/audio", {
          method: "POST",
          body: formData,
        });

        const { audioUrl } = await uploadRes.json();
        uploadedAudioUrl = audioUrl;
      }

      // Prevent submitting nothing
      if (!uploadedAudioUrl && !comment.trim()) {
        alert("Please provide a comment or an audio recording.");
        return;
      }

      const newRecord = {
        behavior: comment ? "Comment" : "Recording",
        audio: uploadedAudioUrl,
        comment: comment.trim(),
      };

      const updatedRecords = [...incidentRecords, newRecord];
      setIncidentRecords(updatedRecords);
      setComment("");
      setAudioURL(null);
    } catch (err) {
      console.error("Audio upload or record submission failed:", err);
    }
  };

  // Handle Deleting a Record
  const handleDeleteRecord = (index) => {
    const updatedRecords = incidentRecords.filter((_, i) => i !== index);
    setIncidentRecords(updatedRecords);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Make the API request
        const response = await axios.get("/api/auth/users");
        // Filter users with roleid === 4 and update state
        const filteredUsers = response.data.filter((usera) => usera.id !== user?.id);
        setUsers(filteredUsers);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    fetchUsers();
  }, [user?.id]);

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Cause For Concern</Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item lg={8} md={8}>
          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black" }}>
            Details of incident / concern
          </Typography>

          <Grid container spacing={2}>
            <Grid item lg={3}>
              <Typography sx={{ fontSize: "14px" }} align='left'>
                Time of concern
              </Typography>
              <TextField
                fullWidth
                type='time'
                size='small'
                value={incidentTime}
                onChange={(e) => setIncidentTime(e.target.value)}
                sx={{ mb: 3 }}
              />
            </Grid>
            <Grid item lg={3}>
              <Typography sx={{ fontSize: "14px" }} align='left'>
                Date of concern
              </Typography>
              <TextField
                type='date'
                fullWidth
                size='small'
                value={dateInformed}
                onChange={(e) => setDateInformed(e.target.value)}
                sx={{ mb: 3 }}
              />
            </Grid>
            <Grid item lg={6}>
              <Typography sx={{ fontSize: "14px" }} align='left'>
                Where did it happen? *
              </Typography>
              <Autocomplete
                id='name'
                options={["indoor", "outdoor"]} // Use the full users array (not just names)
                value={place}
                onChange={(event, newValue) => {
                  setPlaced(newValue); // Store the selected user object
                }}
                renderInput={(params) => <TextField {...params} placeholder='Select / Type' />}
                fullWidth
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "20px", // Adjust the height
                    padding: "2px", // Reduce padding
                  },
                }}
              />
            </Grid>
          </Grid>
          <Typography sx={{ fontSize: "14px", fontWeight: "bold", color: "black", mb: 2 }}>
            Witness / Involved for concern
          </Typography>

          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#E3F2FD" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#1976D2" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#1976D2" }}>Involved/Witness</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#1976D2" }}>Adult/Child</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {witnessData.map((witness, index) => (
                  <TableRow key={index}>
                    <TableCell>{witness.name}</TableCell>
                    <TableCell>{witness.role}</TableCell>
                    <TableCell sx={{ color: "#333" }}>{witness.category}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <br />
          <Button color='primary' onClick={() => setDialogOpen(true)}>
            <AddIcon /> Add Another Involved / Witness
          </Button>

          {/* Reusable Dialog for Adding Witness */}
          <ReusableDialog
            isOpen={dialogOpen}
            onClose={() => setDialogOpen(false)}
            title='Add Another Involved / Witness'
            onSubmit={handleProceed}
            submitText='Proceed'
            cancelText='Cancel'
          >
            {/* Description */}
            <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
              Add the relevant details to the fields to add another person to the involved parties.
            </Typography>

            {/* Name Field */}
            <Typography sx={{ fontSize: "14px", mb: 1, color: "black" }}>Name</Typography>

            <Autocomplete
              id='name'
              options={users} // Use the full users array (not just names)
              getOptionLabel={(option) => option.username} // Display username
              value={selectedUser}
              onChange={(event, newValue) => {
                setSelectedUser(newValue); // Store the selected user object
              }}
              renderInput={(params) => <TextField {...params} placeholder='Select a user' />}
              fullWidth
            />
            <Grid container>
              <Grid item lg={10}>
                {/* Involved / Witness Selection */}
                <Typography sx={{ fontSize: "14px", mb: 1, color: "black" }}>Involved / Witness</Typography>
                <FormControl>
                  <RadioGroup
                    row
                    value={role}
                    onChange={(e) => handleRoleChange(e, e.target.value)}
                    sx={{ mb: 3, gap: 2 }}
                  >
                    <FormControlLabel
                      value='Involved'
                      control={<Radio sx={{ "&.Mui-checked": { color: "#1751D0" } }} />}
                      label='Involved'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        mr: 0,
                        ml: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": { backgroundColor: "#F5F5F5" },
                      }}
                    />
                    <FormControlLabel
                      value='Witness'
                      control={<Radio sx={{ "&.Mui-checked": { color: "#1751D0" } }} />}
                      label='Witness'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        mr: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": { backgroundColor: "#F5F5F5" },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item lg={8}>
                {/* Adult / Child Selection */}
                <Typography sx={{ fontSize: "14px", mb: 1, color: "black" }}>Adult / Child</Typography>
                <FormControl>
                  <RadioGroup
                    row
                    value={category}
                    onChange={(e) => handleCategoryChange(e, e.target.value)}
                    sx={{ mb: 3, gap: 2 }}
                  >
                    <FormControlLabel
                      value='Adult'
                      control={<Radio sx={{ "&.Mui-checked": { color: "#1751D0" } }} />}
                      label='Adult'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        mr: 0,
                        ml: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": { backgroundColor: "#F5F5F5" },
                      }}
                    />
                    <FormControlLabel
                      value='Child'
                      control={<Radio sx={{ "&.Mui-checked": { color: "#1751D0" } }} />}
                      label='Child'
                      sx={{
                        border: "1px solid #E0E0E0",
                        borderRadius: 1,
                        flex: 1,
                        mr: 0,
                        pl: 1,
                        width: "150px",
                        "&:hover": { backgroundColor: "#F5F5F5" },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </ReusableDialog>

          <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold", mt: 4 }}>
            Immediate Actions Taken
          </Typography>
          {/* Description */}
          <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
            Provide a description of the immediate actions taken in response to the incident.
          </Typography>

          {/* Audio Recording UI */}
          <Box
            sx={{
              border: "2px dashed #ccc",
              padding: 3,
              textAlign: "center",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 180,
              mb: 2,
            }}
          >
            {!audioURL ? (
              <>
                <Typography sx={{ color: "#757575", fontSize: "16px", mb: 1 }}>Try Recording Your Sound Now</Typography>
                <MicIcon sx={{ fontSize: 50, color: "#757575", mb: 1 }} />
                <Typography sx={{ fontSize: "14px", color: "#757575", mb: 1 }}>
                  To start recording press the <b>“Start Recording”</b> button and start recording!
                </Typography>
                <Button variant='contained' color='primary' onClick={recording ? stopRecording : startRecording}>
                  {recording ? "Stop Recording" : "Start Recording"}
                </Button>
              </>
            ) : (
              <>
                <audio controls src={audioURL} />
                <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={() => setAudioURL(null)}>
                  Record Again
                </Button>
              </>
            )}
          </Box>

          <Typography sx={{ fontSize: "14px", mb: 1 }}>Comment</Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder='Enter your comment here...'
            variant='outlined'
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {(audioURL || comment) && (
            <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={handleSubmitRecord}>
              Submit
            </Button>
          )}
        </Grid>
        <Grid item lg={4} md={4}>
          {/*  Non - Physical Intervention Details Section - Recorded Entries */}
          <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold", mb: 2 }}>
              Cause For Concern
            </Typography>
            <Typography sx={{ fontSize: "14px", mb: 3 }}>Sample Description</Typography>

            {incidentRecords.map((record, index) => (
              <Paper
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: 1,
                  mb: 1,
                  borderRadius: 2,
                  border: "1px solid #ccc",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "black" }}>
                  {record.audio ? (
                    record.behavior
                  ) : (
                    <>
                      {record.behavior}
                      <br />
                      {record.comment}
                    </>
                  )}
                </Typography>

                <Box>
                  {record.audio && (
                    <>
                      <IconButton onClick={() => window.open(record.audio, "_blank")}>
                        <MicIcon color='primary' />
                      </IconButton>
                      <IconButton onClick={() => window.open(record.audio, "_blank")}>
                        <CloudDownloadIcon color='primary' />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={() => handleDeleteRecord(index)}>
                    <DeleteIcon color='error' />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        </Grid>
      </Grid>

      {/* Footer Actions */}
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
          display: "flex",
          gap: 2,
        }}
      >
        <Button
          variant='outlined'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            prevStep();
          }}
        >
          Previous
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            nextStep(); // Call the function properly
            updateFormData({ witnessInfo: incidentRecords, witnessData, incidentTime, dateInformed, place }); // Call the function properly
          }}
          disabled={witnessData.length === 0 || incidentTime === "" || dateInformed === "" || place === ""}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default CauseForWitness;
